import { useCallback, useMemo } from 'react';
import { useMatches } from 'react-router-dom';
import { StrictRouteMatch } from 'src/types/reactRouter';
import { GATED_CLIENT_ID, useClientId } from 'src/contexts/ClientContext';
import { getDeviceType } from 'src/utils/deviceDetection';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { safePublish } from './safePublish';
import { FeatureName, ActionType } from './metricsFields';

export interface UserMetricEvent {
  // The feature which was interacted with by the user
  feature: FeatureName;
  // Optional additional metadata to publish within the same action (e.g., url of a link clicked)
  metadata?: Record<string, string | number | undefined>;
}

const getPublisherMethodName = (pageName: FeatureName, actionType: ActionType) => (
  `${pageName}.${actionType}`
);

/**
 * This hook is used to publish metrics for user events with contextual awareness of the current page.
 * All user events should be published through this hook.
 */
export const useMetrics = () => {
  const matches = useMatches() as StrictRouteMatch[];
  const activeRoute: StrictRouteMatch = matches[matches.length - 1];
  const { pageName } = activeRoute.handle;
  const clientId = useClientId();

  const publishMetric = useCallback((event: UserMetricEvent, actionType: ActionType) => {
    // Campus-only Gated/Ungated split, matching usePagePerformance. Widget pages resolve clientId
    // from an embedder-controlled URL param, so they classify as Ungated here; widget traffic is
    // covered by the API-layer Surface dimension.
    const surface = clientId === GATED_CLIENT_ID ? 'Gated' : 'Ungated';

    const publishTo = (methodName: string): void => safePublish(
      methodName,
      (publisher) => {
        publisher.publishCounterMonitor(event.feature, 1);

        const metadata = { ...event.metadata, clientId, deviceType: getDeviceType() };
        Object.entries(metadata).forEach(([key, value]) => {
          if (key && value != null) publisher.publishStringTruncate(key, value);
        });
      },
      (e) => {
        ClientLogger.warn(ClientLog.MetricsFailure, { feature: event.feature, error: String(e) });
      },
    );

    // Dual-emit for Volume: the un-suffixed action keeps existing iGraph graphs/dashboards working;
    // the surface-suffixed action enables the per-page Gated/Ungated Volume split (SU-BACKLOG-1248
    // #3). The base and surface actions are separate counters covering the same events, so never
    // sum across both. Applied to View only; Click is intentionally left un-split.
    const baseMethodName = getPublisherMethodName(pageName, actionType);
    publishTo(baseMethodName);
    if (actionType === ActionType.View) {
      publishTo(`${baseMethodName}.${surface}`);
    }
  }, [pageName, clientId]);

  const clickMetric = useCallback(
    (event: UserMetricEvent) => publishMetric(event, ActionType.Click),
    [publishMetric],
  );

  const viewMetric = useCallback(
    (event: UserMetricEvent) => publishMetric(event, ActionType.View),
    [publishMetric],
  );

  return useMemo(() => ({ clickMetric, viewMetric }), [clickMetric, viewMetric]);
};

export default useMetrics;
